<template>
  <div class="position-relative flex-grow-1 col-12 tournament-participants">
    <Widget
      v-model="searchTerm"
      :title="$$t('Participants Applications', 'applications-list')"
      class="mb-5"
      @input="onFilterChange"
    />
    <HeraDummyList
      v-if="showStub"
      class="mt-4"
      size="200"
      icon-name="dummy/members"
      :header-text="$$t('You have no participants yet', 'grid_empty')"
    />
    <div v-else class="tournament-participants-list mt-5">
      <Paginated
        v-bind="{
          totalItems,
          hidePagination: !players.length,
          pageSize,
          inContainer: true,
        }"
        class="mb-5"
        @change="fetchList"
      >
        <Table
          :columns="visibleColumns"
          :items="players"
          :loading="isLoading"
          :max-items-length="totalItems"
          :dummy-props="{
            size: 200,
            iconName: 'dummy/members',
            headerText: $t(
              '_web_tournament_players_empty',
              'You have no participants yet'
            ),
          }"
          class="all-participants-table mb-5 col"
        >
          <template #head-item="{ column }">
            <PlayersTableHeadCell
              :column-name="column.name"
              :sort-icon="
                sortFields.field !== transformField(column.name)
                  ? 'sort'
                  : sortFields.order === 'incr'
                  ? 'sort-asc'
                  : 'sort-dsc'
              "
              @sort="handleSort"
            />
          </template>
          <template #body-item="{ item, column }">
            <PlayersTableCell
              :column-name="column.name"
              :column-label="column.label"
              :row="item"
              @chat="startChat"
            />
          </template>
        </Table>
      </Paginated>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { translate } from '@/mixins/translate';
import listMixin from '@/mixins/paginatedLists';
import { tournamentHelpers } from '@/mixins/tournaments/tournamentHelpers';
import Widget from '@/views/tournaments/management/summaryWidgets/Widget';
import HeraDummyList from '@/components/common/HeraDummyList';
import Paginated from '@/components/common/Paginated';
import Table from '@/components/molecules/Table';
import PlayersTableHeadCell from '@/views/tournaments/management/participants/components/PlayersTableHeadCell';
import PlayersTableCell from '@/views/tournaments/management/participants/components/PlayersTableCell';
const HIDDEN_COLUMNS = [
  'approvedTo',
  'attributes',
  'avatar',
  'applicationDate',
  'applicationTime',
];
export default {
  name: 'ParticipantsRegData',
  components: {
    Widget,
    HeraDummyList,
    Paginated,
    Table,
    PlayersTableHeadCell,
    PlayersTableCell,
  },
  mixins: [
    ...listMixin,
    translate('_web_tournament_participants'),
    tournamentHelpers,
  ],
  data() {
    return {
      searchTerm: '',
      players: [],
      customFields: null,
      customClanFields: null,
      defaultFields: null,
      sortFields: {
        field: null,
        order: null,
      },
      columns: [],
      columnsSettings: [],
      defaultColumns: {
        applicationDatetime: {
          name: 'applicationDatetime',
          is_visible: true,
          _tdClass: 'all-participants-table__datetime-column',
          _thClass: 'all-participants-table__datetime-column',
        },
        size: {
          name: 'size',
          is_visible: true,
          _tdClass: 'all-participants-table__chat-column',
          _thClass: 'all-participants-table__chat-column',
        },
        userLogin: {
          name: 'userLogin',
          is_visible: true,
          _tdClass: 'all-participants-table__username-column',
          _thClass: 'all-participants-table__username-column',
        },
        chat: {
          name: 'chat',
          is_visible: true,
          _tdClass: 'all-participants-table__chat-column',
          _thClass: 'all-participants-table__chat-column',
        },
      },
    };
  },
  computed: {
    ...mapGetters(['playersTableSettings']),
    showStub() {
      return (
        this.isDraft ||
        this.isAnnounced ||
        (this.isRegStarted &&
          !this.players.length &&
          !this.isLoading &&
          !this.searchTerm)
      );
    },
    allColumns() {
      return [
        ...this.defaultFields,
        ...(!this.properties
          ? []
          : this.properties.is_clan
          ? this.customClanFields
          : this.customFields),
      ].map(c => ({
        ...c,
        name: this.$lodash.lowerFirst(c.associate_field || c.label),
      }));
    },
    visibleColumns() {
      return this.columns.filter(el =>
        this.properties.is_clan
          ? ![...HIDDEN_COLUMNS, 'username', 'userLogin'].includes(el.name)
          : !HIDDEN_COLUMNS.includes(el.name)
      );
    },
  },
  created() {
    this.fetchTournamentCustomFieldsAll({ id: this.tournamentID })
      .then(data => {
        this.customFields = data.custom_fields;
        this.customClanFields = data.custom_clan_fields;
        this.defaultFields = data.default_field;
      })
      .then(this.fetchList)
      .then(this.initializeColumns);
  },
  methods: {
    ...mapActions([
      'startTournamentChat',
      'fetchTournamentPlayers',
      'fetchColumnsSettings',
      'fetchTournamentCustomFieldsAll',
    ]),
    fetchList(pageNumber) {
      this.pageIndex = pageNumber;
      let orderByPostfix = this.sortFields.order === 'desc' ? ':desc' : '';
      return this.getPageData(
        'players',
        this.fetchTournamentPlayers({
          id: this.tournamentID,
          query: this.paramsWithPageNumber({
            order_by: this.sortFields.field
              ? `${this.sortFields.field}${orderByPostfix}`
              : null,
            search_term: this.searchTerm,
            full_registration_data: true,
          }),
        })
      );
    },
    transformField(field) {
      const transformedField = field.replace(/\s+/g, '');
      return (
        transformedField.charAt(0).toLowerCase() + transformedField.substring(1)
      );
    },
    clearPlayersState() {
      this.pageIndex = 0;
      this.players = [];
      this.activeRow = null;
    },
    onFilterChange() {
      this.clearPlayersState();
      this.fetchList(this.tab);
    },
    startChat(player) {
      this.startTournamentChat(player).catch(this.errorNotify);
    },
    initializeColumns() {
      let columnsClone = this.$lodash.cloneDeep(this.allColumns);
      const isClan = this.properties.is_clan;
      const isNameColumn = c => c.name === (isClan ? 'clan_name' : 'username');
      const usernameColumn = columnsClone.find(isNameColumn);

      usernameColumn._tdClass = 'all-participants-table__username-column';
      usernameColumn._thClass = 'all-participants-table__username-column';
      const orderedColumns = columnsClone
        .filter(c => !isNameColumn(c))
        .sort((a, b) => a.index - b.index);
      this.columns = [
        usernameColumn,
        this.defaultColumns.userLogin,
        this.defaultColumns.applicationDatetime,
        this.defaultColumns.chat,
        ...orderedColumns,
      ];
    },
    handleSort(field) {
      const transformedField = this.transformField(field);

      if (
        this.sortFields.order === 'incr' &&
        this.sortFields.field === transformedField
      ) {
        this.sortFields.order = 'desc';
      } else {
        this.sortFields.order = 'incr';
      }
      this.sortFields.field = transformedField;
      this.onFilterChange();
    },
  },
};
</script>
